import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { SendHorizontal, ArrowLeft, CheckCircle, MessagesSquare, Trash2, MoreVertical, EyeOff, ShieldAlert, ShieldCheck, Archive } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from "@/lib/utils";
import ConversationsList from '@/components/messages/ConversationsList';
import EmptyConversationState from '@/components/messages/EmptyConversationState';
import { UIMessage } from '@/hooks/useMessages';
import { Conversation } from '@/hooks/useConversations';
import { PartnerDetails } from '@/pages/Messages';
import { useLanguage } from '@/context/language-context';
import BookingDialog from '@/components/messages/BookingDialog';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuLabel, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu";
import { useToast } from '@/hooks/use-toast';
import { createClient } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

interface MessagesDesktopViewProps {
  conversations: Conversation[];
  activeConversation: Conversation | null;
  partnerDetails: PartnerDetails | null;
  messages: UIMessage[];
  isLoadingConversations: boolean;
  isLoadingMessages: boolean;
  onSelectConversation: (conversationId: string) => void;
  onSendMessage: (message: string) => Promise<void>;
  deleteMessage?: (messageId: string) => Promise<void>;
  onArchiveConversation: (conversationId: string) => Promise<void>;
  userId?: string;
  onSendBookingRequest: (date: Date, timeSlot: string, duration?: number) => Promise<void>;
  isBookingModalOpen: boolean;
  setIsBookingModalOpen: (open: boolean) => void;
}

const MessagesDesktopView: React.FC<MessagesDesktopViewProps> = ({
  conversations,
  activeConversation,
  partnerDetails,
  messages,
  isLoadingConversations,
  isLoadingMessages,
  onSelectConversation,
  onSendMessage,
  deleteMessage,
  onArchiveConversation,
  userId,
  onSendBookingRequest,
  isBookingModalOpen,
  setIsBookingModalOpen
}) => {

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollViewportRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { getUrlWithLanguage, t } = useLanguage();
  const { toast } = useToast();

  useEffect(() => {
    const viewport = scrollViewportRef.current;
    if (!viewport || messages.length === 0) {
      return;
    }

    // Scroll to bottom whenever messages change
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView();
    }, 0);
  }, [messages]);
  
  const handleInternalSendMessage = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const input = e.currentTarget.elements.namedItem('messageInput') as HTMLInputElement;
    const content = input.value.trim();
    if (content) {
      try {
        await onSendMessage(content);
        input.value = '';
      } catch (error) {
        console.error('[MessagesDesktopView] Send message failed:', error);
      }
    }
  };

  const handleHideConversation = async () => {
    if (!activeConversation || !activeConversation.id) return;
    try {
      await onArchiveConversation(activeConversation.id);
    } catch (error: any) {
      console.error('Failed to hide conversation:', error);
      toast({ 
          title: t('common:common.error') || 'Erreur', 
          description: t('messages:messages.hide_conversation_error'), 
          variant: 'destructive' 
      });
    }
  };

  return (
    <div className="h-full py-6 px-4 max-w-7xl mx-auto">
      <Card className="shadow-xl overflow-hidden border-pink-100 h-[calc(100vh-var(--header-height,80px)-3rem)] grid grid-cols-12 bg-white rounded-xl">
        <div className="col-span-4 xl:col-span-3 border-r border-gray-100 bg-white flex flex-col rounded-l-xl">
         <div className="p-4 border-b border-gray-100 flex justify-between items-center flex-shrink-0">
            <h2 className="text-xl font-bold text-pink-800">{t('messages:messages.title') || 'Messages'}</h2>
         </div>
          <ScrollArea className="flex-grow">
             <ConversationsList 
                conversations={conversations}
                isLoading={isLoadingConversations}
                activeConversationId={activeConversation?.id}
                onSelectConversation={onSelectConversation}
                onArchiveConversation={onArchiveConversation}
             />
          </ScrollArea>
        </div>
        
        <div className="col-span-8 xl:col-span-9 flex flex-col bg-gradient-to-br from-pink-50/50 via-white to-purple-50/50 rounded-r-xl overflow-hidden">
          {activeConversation && partnerDetails ? (
              <>
                <div className="bg-white/80 backdrop-blur-sm border-b border-pink-100 p-3 flex items-center justify-between gap-4 sticky top-0 z-10 shadow-sm">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <Avatar className="h-11 w-11 border-2 border-pink-200 shadow-sm flex-shrink-0">
                      <AvatarImage 
                        src={partnerDetails.avatar_url || undefined} 
                        alt={partnerDetails.display_name} 
                        className="object-cover"
                      />
                      <AvatarFallback className="bg-pink-100 text-pink-600 font-semibold">{partnerDetails.display_name?.substring(0, 1).toUpperCase() || 'P'}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <Link 
                        to={getUrlWithLanguage(`/profile/${partnerDetails.id}`)} 
                        className="text-base font-semibold text-gray-800 hover:text-pink-700 truncate block"
                        title={partnerDetails.display_name}
                      >
                        {partnerDetails.display_name || 'Utilisateur'}
                      </Link>
                      <div className="flex items-center gap-2 text-xs text-gray-600 mt-0.5 flex-wrap">
                        {partnerDetails.age && <span>{partnerDetails.age} ans</span>}
                        {partnerDetails.city && <span>· {partnerDetails.city}</span>}
                        {partnerDetails.nationality && <span>· {partnerDetails.nationality}</span>}
                         {partnerDetails.is_verified && (
                           <CheckCircle className="h-4 w-4 text-pink-500 inline-block ml-1" title={t('profile:profile.verified') || "Vérifié"} />
                         )}
                      </div>
                      <Badge variant={partnerDetails.is_online ? 'success' : 'outline'} className={`text-xs h-5 px-1.5 mt-1 ${partnerDetails.is_online ? 'bg-green-100 text-green-700 border-green-200' : 'bg-gray-100 border-gray-200'}`}>
                          {partnerDetails.is_online ? (t('messages:messages.status_online') || 'En ligne') : (t('messages:messages.status_offline') || 'Hors ligne')}
                        </Badge>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                     {partnerDetails.role === 'escort' && (
                         <Button 
                            variant="outline"
                            size="sm"
                            className="border-pink-300 text-pink-700 hover:bg-pink-50 hover:text-pink-800"
                            onClick={() => setIsBookingModalOpen(true)}
                         >
                           {t('messages:messages.book_button') || 'Réserver'}
                         </Button>
                     )} 
                     <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="h-5 w-5" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuLabel>{t('common:common.actions') || 'Actions'}</DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={handleHideConversation}>
                            <Archive className="mr-2 h-4 w-4" />
                            <span>{t('messages:messages.archive') || 'Masquer la conversation'}</span>
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                   </div>
                </div>

                <ScrollArea viewportRef={scrollViewportRef} className="flex-1 px-5 pt-4 overflow-y-auto">
                  <div className="space-y-4 pb-4"> 
                    {isLoadingMessages && (
                       <div className="flex justify-center items-center h-full">
                          <p className="text-muted-foreground">{t('common:common.loading') || 'Chargement...'}</p>
                       </div>
                    )}
                    {!isLoadingMessages && messages.length === 0 && (
                      <div className="flex justify-center items-center h-full pt-16">
                         <p className="text-gray-500 italic">{t('messages:messages.no_messages') || 'Aucun message. Dites bonjour !'}</p>
                      </div>
                    )}
                    {(() => { // IIFE to manage state within map
                      let lastMessageDate: string | null = null;
                      return !isLoadingMessages && messages.map((msg) => {
                          const isUserSender = msg.senderId === userId;
                          const isAiSender = msg.senderType === 'ai';

                          let currentMessageDateStr: string | null = null;
                          let dateSeparator: React.ReactNode = null;
                          
                          try {
                              // Use only createdAt, assuming it's a full valid date string (e.g., ISO 8601)
                              const messageDateSource = msg.createdAt; 
                              
                              // Ensure createdAt is a valid string before parsing
                              if (typeof messageDateSource === 'string' && messageDateSource) {
                                  const currentMessageDate = new Date(messageDateSource);
                                  if (!isNaN(currentMessageDate.getTime())) {
                                      currentMessageDateStr = currentMessageDate.toLocaleDateString('fr-FR'); // Format JJ/MM/AAAA
                                      
                                      if (currentMessageDateStr !== lastMessageDate) {
                                          dateSeparator = (
                                              <div className="flex items-center justify-center my-4" key={`date-${currentMessageDateStr}`}>
                                                  <span className="h-px bg-gray-300 flex-grow max-w-[100px]"></span>
                                                  <span className="mx-3 text-xs text-gray-500 font-medium">
                                                      {currentMessageDateStr}
                                                  </span>
                                                  <span className="h-px bg-gray-300 flex-grow max-w-[100px]"></span>
                                              </div>
                                          );
                                          lastMessageDate = currentMessageDateStr;
                                      }
                                  } else {
                                      console.warn("Could not parse date from createdAt:", msg.id, messageDateSource);
                                  }
                              } else if (msg.createdAt) {
                                  // Log if createdAt exists but is not a string (unexpected)
                                  console.warn("msg.createdAt is not a string:", msg.id, msg.createdAt);
                              }
                              // If createdAt is missing or invalid, no date separator will be generated for this message.
                          } catch (e) {
                              console.error("Error processing date for message:", msg.id, e);
                          }

                          return (
                              <React.Fragment key={msg.id}>
                                  {dateSeparator}
                                  <div 
                                      className={cn(
                                          "flex items-end gap-2 group",
                                          isUserSender ? "justify-end" : "justify-start"
                                      )}
                                  >
                                      {isUserSender && deleteMessage && (
                                          <button 
                                              onClick={() => deleteMessage(msg.id)}
                                              className="opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-pink-600"
                                              title={t('messages:messages.delete_message') || "Supprimer ce message"}
                                          >
                                              <Trash2 className="h-4 w-4" />
                                          </button>
                                      )}
                                      <div 
                                          className={cn(
                                              "rounded-2xl px-4 py-2 shadow-md max-w-[70%] md:max-w-[60%]",
                                              isUserSender 
                                                  ? 'bg-gradient-to-br from-pink-500 to-rose-500 text-white rounded-br-none'
                                                  : isAiSender 
                                                      ? 'bg-gradient-to-br from-purple-50 to-purple-100 text-purple-900 rounded-bl-none border border-purple-200'
                                                      : 'bg-white text-gray-800 rounded-bl-none border border-gray-200'
                                          )}
                                      >
                                          <p className="text-sm break-words whitespace-pre-wrap">{msg.content}</p>
                                          <p className={cn(
                                              "text-xs mt-1 opacity-70", 
                                              isUserSender ? "text-pink-100 text-right" : 
                                              isAiSender ? "text-purple-500 text-right" :
                                              "text-gray-500 text-right"
                                          )}>
                                              {msg.timestamp} {/* Keep original timestamp for time display */}
                                          </p>
                                      </div>
                                      {!isUserSender && msg.senderType !== 'ai' && (
                                          <div className="w-4"></div> // Spacer
                                      )}
                                  </div>
                              </React.Fragment>
                          );
                      });
                    })()}
                  </div>
                  <div ref={messagesEndRef} className="h-1" />
                  <ScrollBar orientation="vertical" />
                </ScrollArea>

                <div className="p-4 bg-white/70 backdrop-blur-sm border-t border-pink-100 sticky bottom-0 shadow- ऊपर">
                   <form onSubmit={handleInternalSendMessage} className="flex items-center gap-3">
                    <Input 
                      name="messageInput"
                      type="text" 
                      placeholder={t('messages:messages.input_placeholder') || "Écrire un message..."} 
                      className="flex-grow bg-gray-100 border-gray-300 rounded-full px-4 py-2 focus:border-pink-400 focus:ring-pink-400"
                      autoComplete="off"
                      disabled={isLoadingMessages}
                    />
                    <Button 
                      type="submit"
                      size="icon"
                      className="shrink-0 rounded-full bg-gradient-to-br from-pink-500 to-rose-500 text-white hover:from-pink-600 hover:to-rose-600 disabled:opacity-50"
                      disabled={isLoadingMessages}
                    >
                      <SendHorizontal className="h-5 w-5"/>
                    </Button>
                  </form>
                </div>
              </>
          ) : (
            <EmptyConversationState />
          )}
        </div>
      </Card>
      
      <BookingDialog
        isOpen={isBookingModalOpen} 
        onOpenChange={setIsBookingModalOpen} 
        onBookAppointment={onSendBookingRequest} 
        escortId={partnerDetails?.id || ''}
      />
    </div>
  );
};

export default MessagesDesktopView;
